import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiService } from '@/services/api-service';
import { useAuth } from './AuthContext';
import { SHAREPOINT_CONFIG_UPDATED_EVENT } from '@/utils/sharepoint-env';

interface SharePointConfig {
  TENANT_ID: string;
  CLIENT_ID: string;
  CLIENT_SECRET: string;
  SITE_HOSTNAME: string;
  SITE_PATH: string;
  DOC_LIBRARY: string;
  FY_YEAR: string;
  USERNAME: string;
}

interface SharePointConfigContextType {
  config: SharePointConfig | null;
  isLoading: boolean;
  loadConfig: (engagementId: string, clientName: string, engagementName: string) => Promise<void>;
  clearConfig: () => void;
}

const SharePointConfigContext = createContext<SharePointConfigContextType | undefined>(undefined);

export function SharePointConfigProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [config, setConfig] = useState<SharePointConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const syncConfigFromLocalStorage = useCallback(() => {
    const storedConfig = localStorage.getItem('sharepoint_config');
    if (!storedConfig) return;
    try {
      setConfig(JSON.parse(storedConfig));
    } catch (error) {
      console.error('Error loading SharePoint config:', error);
    }
  }, []);

  // Load config from localStorage on mount and when offline ZIP / engagement updates it
  useEffect(() => {
    syncConfigFromLocalStorage();
    const onUpdated = () => syncConfigFromLocalStorage();
    window.addEventListener(SHAREPOINT_CONFIG_UPDATED_EVENT, onUpdated);
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'sharepoint_config') onUpdated();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(SHAREPOINT_CONFIG_UPDATED_EVENT, onUpdated);
      window.removeEventListener('storage', onStorage);
    };
  }, [syncConfigFromLocalStorage]);

  const loadConfig = async (engagementId: string, clientName: string, engagementName: string) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    setIsLoading(true);
    try {
      // Use the dedicated SharePoint config endpoint which follows the full chain:
      // engagement → audit_client → firm
      // This correctly resolves doc_library and fy_year for BOTH UUID and numeric engagement IDs,
      // and returns all values properly decrypted.
      let spConfig: {
        tenant_id: string;
        client_id: string;
        client_secret: string;
        site_hostname: string;
        site_path: string;
        doc_library: string;
        fy_year: string;
      } | null = null;

      try {
        spConfig = await apiService.getSharePointConfig(engagementId);
        console.log('SharePoint config received from dedicated endpoint:', {
          tenant_id: spConfig.tenant_id ? `${spConfig.tenant_id.substring(0, 8)}...` : 'MISSING',
          client_id: spConfig.client_id ? `${spConfig.client_id.substring(0, 8)}...` : 'MISSING',
          client_secret: spConfig.client_secret ? `${spConfig.client_secret.substring(0, 8)}...` : 'MISSING',
          site_hostname: spConfig.site_hostname,
          site_path: spConfig.site_path,
          doc_library: spConfig.doc_library,
          fy_year: spConfig.fy_year,
        });
      } catch (spConfigError: any) {
        console.warn('getSharePointConfig endpoint failed, falling back to getFirm():', spConfigError.message);
        // Fallback: use getFirm() + passed-in clientName/engagementName
        const firm = await apiService.getFirm();
        if (!firm.tenant_id || firm.tenant_id.trim() === '') {
          throw new Error('Firm configuration incomplete: tenant_id is missing or empty in the database.');
        }
        if (!firm.client_id || firm.client_id.trim() === '') {
          throw new Error('Firm configuration incomplete: client_id is missing or empty in the database.');
        }
        if (!firm.client_secret || firm.client_secret.trim() === '') {
          throw new Error('Firm configuration incomplete: client_secret is missing or empty in the database.');
        }
        spConfig = {
          tenant_id: firm.tenant_id,
          client_id: firm.client_id,
          client_secret: firm.client_secret,
          site_hostname: firm.site_hostname || '',
          site_path: firm.site_path || '',
          doc_library: clientName,
          fy_year: engagementName,
        };
      }

      // Validate required firm-level fields
      if (!spConfig.tenant_id || spConfig.tenant_id.trim() === '') {
        throw new Error('Firm configuration incomplete: tenant_id is missing or empty in the database.');
      }
      if (!spConfig.client_id || spConfig.client_id.trim() === '') {
        throw new Error('Firm configuration incomplete: client_id is missing or empty in the database.');
      }
      if (!spConfig.client_secret || spConfig.client_secret.trim() === '') {
        throw new Error('Firm configuration incomplete: client_secret is missing or empty in the database.');
      }

      // Build complete SharePoint config:
      // - Firm-level config (TENANT_ID, CLIENT_ID, CLIENT_SECRET, SITE_HOSTNAME, SITE_PATH)
      // - DOC_LIBRARY = resolved client name (from dedicated endpoint or fallback)
      // - FY_YEAR = ALWAYS the engagement name, so each engagement uses its own SharePoint folder.
      //   The API endpoint may return a firm-level generic fy_year (e.g. "Beta Test") which is wrong
      //   because different engagements must map to different folders. Using engagementName ensures
      //   consistency between dev mode (which reads from scripts/sharepoint_config.json, manually set
      //   to the engagement name) and exe mode.
      // - USERNAME = user_name (from current user)
      const newConfig: SharePointConfig = {
        TENANT_ID: spConfig.tenant_id,
        CLIENT_ID: spConfig.client_id,
        CLIENT_SECRET: spConfig.client_secret,
        SITE_HOSTNAME: spConfig.site_hostname,
        SITE_PATH: spConfig.site_path,
        DOC_LIBRARY: spConfig.doc_library,
        FY_YEAR: engagementName, // Always use the engagement name as the SharePoint folder name
        USERNAME: user.user_name, // Current user's username
      };

      // Store config in localStorage (for renderer process) and file (for main process)
      // This ensures values don't change while user is inside the engagement
      // Only reload when clicking on a different engagement
      setConfig(newConfig);
      
      // Store in localStorage for renderer process
      localStorage.setItem('sharepoint_config', JSON.stringify(newConfig));
      window.dispatchEvent(new Event(SHAREPOINT_CONFIG_UPDATED_EVENT));
      
      // Also store in file for Electron main process (Node.js)
      // This allows the main process to access config even though it doesn't have localStorage
      // IMPORTANT: We must await this to ensure the config file is written before returning
      // Otherwise, IPC handlers might try to read the config before it's saved
      try {
        // Use the exposed sharePointAPI from contextBridge
        if (typeof window !== 'undefined' && (window as any).sharePointAPI?.storeConfig) {
          const result = await (window as any).sharePointAPI.storeConfig(newConfig);
          if (result?.success) {
            console.log('SharePoint config stored in main process file successfully');
          } else {
            console.warn('Failed to store config in main process:', result?.error);
            // Don't throw here - config is in localStorage, which is sufficient for renderer process
          }
        } else {
          console.warn('sharePointAPI.storeConfig not available - config will only be in localStorage');
        }
      } catch (error) {
        // IPC might not be available in non-Electron environment, that's okay
        // Log but don't throw - config is already in localStorage
        console.warn('Could not store config in main process (non-critical):', error);
      }
      
      console.log('SharePoint config loaded and stored:', {
        TENANT_ID: newConfig.TENANT_ID.substring(0, 8) + '...',
        CLIENT_ID: newConfig.CLIENT_ID.substring(0, 8) + '...',
        CLIENT_SECRET: newConfig.CLIENT_SECRET ? newConfig.CLIENT_SECRET.substring(0, 8) + '... (' + newConfig.CLIENT_SECRET.length + ' chars)' : 'MISSING',
        DOC_LIBRARY: newConfig.DOC_LIBRARY,
        FY_YEAR: newConfig.FY_YEAR,
        USERNAME: newConfig.USERNAME,
        SITE_HOSTNAME: newConfig.SITE_HOSTNAME,
        SITE_PATH: newConfig.SITE_PATH,
      });
      
      // Verify the config was stored correctly
      const verify = localStorage.getItem('sharepoint_config');
      if (verify) {
        const parsed = JSON.parse(verify);
        console.log('Verification - CLIENT_SECRET in localStorage:', parsed.CLIENT_SECRET ? parsed.CLIENT_SECRET.substring(0, 8) + '... (' + parsed.CLIENT_SECRET.length + ' chars)' : 'MISSING');
      }
    } catch (error) {
      console.error('Error loading SharePoint config:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const clearConfig = () => {
    setConfig(null);
    localStorage.removeItem('sharepoint_config');
  };

  return (
    <SharePointConfigContext.Provider
      value={{
        config,
        isLoading,
        loadConfig,
        clearConfig,
      }}
    >
      {children}
    </SharePointConfigContext.Provider>
  );
}

export function useSharePointConfig() {
  const context = useContext(SharePointConfigContext);
  if (context === undefined) {
    throw new Error('useSharePointConfig must be used within a SharePointConfigProvider');
  }
  return context;
}

